import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, Suspense } from 'react'
import { motion } from 'framer-motion'
import { 
  MessageSquare, 
  AlertCircle,
  Search, 
  Send,
  Plus,
  Filter,
  Clock,
  CheckCircle,
  User,
  Mail,
  Phone,
  Calendar,
  Paperclip,
  Smile,
  MoreHorizontal,
  Users,
  UserCheck
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

interface Message {
  id: string
  sender: string
  recipient: string
  content: string
  timestamp: string
  read: boolean
  type: 'sent' | 'received'
}

interface Contact {
  id: string
  name: string
  email: string
  avatar?: string
  role?: string
  department?: string
  position?: string
  type: 'team' | 'candidate'
  lastMessage?: string
  lastMessageTime?: string
  unreadCount: number
  status: 'active' | 'archived'
  messages: Message[]
  isTeamGroup?: boolean
  teamId?: number
}

interface Conversation {
  id: string
  candidateName: string
  candidateEmail: string
  candidateAvatar?: string
  position: string
  lastMessage: string
  lastMessageTime: string
  unreadCount: number
  status: 'active' | 'archived'
  messages: Message[]
}

interface TeamMemberSummary {
  id: number
  name: string
  email: string
  teamRole?: string
  teamName?: string
}

interface TeamSummary {
  id: number
  name: string
  memberCount?: number
}

interface TeamMembersApiResponse {
  teamMembers?: TeamMemberSummary[]
  teams?: TeamSummary[]
}

interface CandidateSummary {
  id: number
  first_name?: string
  last_name?: string
  name?: string
  email: string
  status?: string
  position?: string
  current_title?: string
}

interface CandidatesApiResponse {
  candidates?: CandidateSummary[]
}


// Loading component for Suspense fallback
function MessagesLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading messages...</p>
      </div>
    </div>
  )
}

// Main messages component that uses useSearchParams
function MessagesContent() {
  const [searchParams] = useSearchParams()
  const candidateParam = searchParams.get('candidate')
  const messagesEnabled = useFeatureFlag('recruiterMessages')
  
  const [contacts, setContacts] = useState<Contact[]>([])
  const [selectedContact, setSelectedContact] = useState<string | null>(
    candidateParam || null
  )
  const [messageContent, setMessageContent] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'all' | 'team' | 'candidates'>('all')
  const [loading, setLoading] = useState(true)

  const currentContact = contacts.find(c => c.id === selectedContact)

  useEffect(() => {
    if (!messagesEnabled) {
      return
    }
    fetchContacts()
  }, [messagesEnabled])

  const fetchContacts = async () => {
    console.log('🚀 fetchContacts function called!')
    try {
      setLoading(true)
      
      // Fetch team members and teams for messaging
      const teamMembersResponse = await apiClient.request<TeamMembersApiResponse>('/messages/team-members')
      
      const candidatesResponse = await apiClient.request<CandidatesApiResponse>('/candidates/list')
      
      const teamContacts: Contact[] = []
      const candidateContacts: Contact[] = []
      
      console.log('Team Members Response:', teamMembersResponse)
      console.log('Candidates Response:', candidatesResponse)
      
      // Process team members and teams data
      if (teamMembersResponse?.success && teamMembersResponse?.data) {
        console.log('Processing team members data...');
        // Add individual team members
        const memberList = teamMembersResponse.data.teamMembers ?? []
        if (memberList.length > 0) {
          for (const member of memberList) {
            teamContacts.push({
              id: `user-${member.id}`,
              name: member.name,
              email: member.email,
              role: member.teamRole,
              department: member.teamName,
              type: 'team',
              unreadCount: 0,
              status: 'active',
              messages: []
            })
          }
        }
        
        // Add teams for group messaging
        const teamList = teamMembersResponse.data.teams ?? []
        if (teamList.length > 0) {
          for (const team of teamList) {
            teamContacts.push({
              id: `team-${team.id}`,
              name: `${team.name} (Team)`,
              email: `team@${team.name.toLowerCase().replace(/\s+/g, '')}.group`,
              role: 'Team Chat',
              department: `${team.memberCount} members`,
              type: 'team',
              unreadCount: 0,
              status: 'active',
              messages: [],
              isTeamGroup: true,
              teamId: team.id
            })
          }
        }
      }
      
      // Process candidates data - skip if API fails
      if (candidatesResponse?.success && candidatesResponse?.data?.candidates) {
        for (const candidate of candidatesResponse.data.candidates) {
          const fullName = candidate.name || [candidate.first_name, candidate.last_name].filter(Boolean).join(' ') || candidate.email
          candidateContacts.push({
            id: `candidate-${candidate.id}`,
            name: fullName,
            email: candidate.email,
            role: candidate.current_title || 'Candidate',
            position: (candidate as any).current_company || 'Looking for opportunities',
            type: 'candidate',
            unreadCount: 0,
            status: 'active',
            messages: []
          })
        }
      } else {
        console.log('No candidates data or candidates API failed')
      }
      
      console.log('Team contacts:', teamContacts)
      console.log('Candidate contacts:', candidateContacts)
      console.log('Total contacts to set:', [...teamContacts, ...candidateContacts])
      
      setContacts([...teamContacts, ...candidateContacts])
      
      // Auto-select first contact if none selected
      if (!selectedContact && (teamContacts.length > 0 || candidateContacts.length > 0)) {
        const firstContact = teamContacts[0] || candidateContacts[0]
        setSelectedContact(firstContact.id)
      }
      
    } catch (error) {
      console.error('Failed to fetch contacts:', error)
      // Set some fallback data for testing
      setContacts([
        {
          id: 'test-1',
          name: 'Test Contact',
          email: 'test@example.com',
          type: 'team',
          role: 'Test Role',
          department: 'Test Department',
          unreadCount: 0,
          status: 'active',
          messages: []
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleSendMessage = async () => {
    if (!messageContent.trim() || !selectedContact) return

    const newMessage: Message = {
      id: Date.now().toString(),
      sender: 'recruiter',
      recipient: currentContact?.type === 'team' ? 'team-member' : 'candidate',
      content: messageContent,
      timestamp: new Date().toISOString(),
      read: true,
      type: 'sent'
    }

    // Add message to local state immediately for better UX
    setContacts(prev => prev.map(contact => 
      contact.id === selectedContact
        ? {
            ...contact,
            messages: [...contact.messages, newMessage],
            lastMessage: messageContent,
            lastMessageTime: newMessage.timestamp
          }
        : contact
    ))

    try {
      if (currentContact?.isTeamGroup) {
        // Send team message
        await apiClient.request('/messages/send-team-message', {
          method: 'POST',
          body: JSON.stringify({
            team_id: currentContact.teamId,
            content: messageContent
          })
        })
      } else if (currentContact?.type === 'team') {
        // Send individual team member message
        const userId = currentContact.id.replace('user-', '')
        await apiClient.request('/messages/send', {
          method: 'POST',
          body: JSON.stringify({
            recipient_id: parseInt(userId),
            recipient_type: 'user',
            content: messageContent,
            message_type: 'text'
          })
        })
      } else {
        // Send candidate message
        const candidateId = currentContact?.id.replace('candidate-', '') || ''
        await apiClient.request('/messages/send', {
          method: 'POST',
          body: JSON.stringify({
            recipient_id: parseInt(candidateId),
            recipient_type: 'candidate',
            content: messageContent,
            message_type: 'text'
          })
        })
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      // Optionally remove the message from local state if sending failed
    }

    setMessageContent('')
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else {
      return date.toLocaleDateString()
    }
  }

  const markAsRead = (contactId: string) => {
    setContacts(prev => prev.map(contact =>
      contact.id === contactId
        ? {
            ...contact,
            unreadCount: 0,
            messages: contact.messages.map(msg => ({ ...msg, read: true }))
          }
        : contact
    ))
  }

  useEffect(() => {
    if (selectedContact) {
      markAsRead(selectedContact)
    }
  }, [selectedContact])

  const getFilteredContacts = () => {
    let filtered = contacts.filter(contact =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (contact.role && contact.role.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (contact.department && contact.department.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (contact.position && contact.position.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    
    if (activeTab === 'team') {
      filtered = filtered.filter(contact => contact.type === 'team')
    } else if (activeTab === 'candidates') {
      filtered = filtered.filter(contact => contact.type === 'candidate')
    }
    
    return filtered
  }

  const filteredContacts = getFilteredContacts()

  if (!messagesEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] bg-white rounded-lg border border-gray-200 text-center space-y-4">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Messages coming soon</h1>
          <p className="text-gray-600 mt-2 max-w-lg">
            This messaging workspace is turned off in the current environment while we finish production hardening.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Contacts List */}
      <div className="w-1/3 border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-semibold text-gray-900">Messages</h1>
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex mb-4 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              All ({contacts.length})
            </button>
            <button
              onClick={() => setActiveTab('team')}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors flex items-center justify-center ${
                activeTab === 'team' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <Users className="w-4 h-4 mr-1" />
              Team ({contacts.filter(c => c.type === 'team').length})
            </button>
            <button
              onClick={() => setActiveTab('candidates')}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors flex items-center justify-center ${
                activeTab === 'candidates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <UserCheck className="w-4 h-4 mr-1" />
              Candidates ({contacts.filter(c => c.type === 'candidate').length})
            </button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Loading contacts...</div>
          ) : filteredContacts.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchQuery ? 'No contacts match your search' : 'No contacts available'}
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <motion.div
                key={contact.id}
                onClick={() => setSelectedContact(contact.id)}
                className={`p-4 cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${
                  selectedContact === contact.id ? 'bg-blue-50 border-blue-200' : ''
                }`}
                whileHover={{ x: 2 }}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    contact.type === 'team' 
                      ? contact.isTeamGroup 
                        ? 'bg-purple-100' 
                        : 'bg-green-100' 
                      : 'bg-blue-100'
                  }`}>
                    {contact.type === 'team' ? (
                      contact.isTeamGroup ? (
                        <Users className="w-5 h-5 text-purple-600" />
                      ) : (
                        <User className="w-5 h-5 text-green-600" />
                      )
                    ) : (
                      <span className="text-sm font-medium text-blue-700">
                        {contact.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {contact.name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          contact.type === 'team' 
                            ? contact.isTeamGroup 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {contact.type === 'team' 
                            ? contact.isTeamGroup 
                              ? 'Team Group' 
                              : 'Team Member' 
                            : 'Candidate'}
                        </span>
                        {contact.lastMessageTime && (
                          <span className="text-xs text-gray-500">
                            {formatTime(contact.lastMessageTime)}
                          </span>
                        )}
                        {contact.unreadCount > 0 && (
                          <span className="bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {contact.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {contact.role} {contact.department && `• ${contact.department}`}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {contact.email}
                    </p>
                    {contact.lastMessage && (
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {contact.lastMessage}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Message View */}
      <div className="flex-1 flex flex-col">
        {currentContact ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentContact.type === 'team' 
                      ? currentContact.isTeamGroup 
                        ? 'bg-purple-100' 
                        : 'bg-green-100' 
                      : 'bg-blue-100'
                  }`}>
                    {currentContact.type === 'team' ? (
                      currentContact.isTeamGroup ? (
                        <Users className="w-5 h-5 text-purple-600" />
                      ) : (
                        <User className="w-5 h-5 text-green-600" />
                      )
                    ) : (
                      <span className="text-sm font-medium text-blue-700">
                        {currentContact.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">
                      {currentContact.name}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {currentContact.role} {currentContact.department && `• ${currentContact.department}`}
                    </p>
                    <p className="text-sm text-gray-500">{currentContact.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full">
                    <Phone className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full">
                    <Calendar className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {currentContact.messages.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 text-sm">Start a conversation with {currentContact.name}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      Send a message to begin communicating with this {
                        currentContact.type === 'team' 
                          ? currentContact.isTeamGroup 
                            ? 'team group (message will be sent to all team members)' 
                            : 'team member' 
                          : 'candidate'
                      }
                    </p>
                  </div>
                </div>
              ) : (
                currentContact.messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'sent' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.type === 'sent'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message.content}</p>
                    <div className={`flex items-center justify-end mt-1 space-x-1 text-xs ${
                      message.type === 'sent' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      <span>{formatTime(message.timestamp)}</span>
                      {message.type === 'sent' && (
                        <CheckCircle className={`w-3 h-3 ${message.read ? 'text-blue-200' : 'text-blue-300'}`} />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full">
                  <Paperclip className="w-4 h-4" />
                </button>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your message..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full">
                  <Smile className="w-4 h-4" />
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!messageContent.trim()}
                  className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No conversation selected</h3>
              <p className="text-gray-600">Choose a conversation from the list to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Main export with Suspense wrapper
export default function MessagesPage() {
  return (
    <Suspense fallback={<MessagesLoading />}>
      <MessagesContent />
    </Suspense>
  )
}
